import moment from 'moment';
import type { ComparisonWindow } from '../api';
import {
  AUTO_RESPONSE_MIN_UNATTENDED_RATING,
  AUTO_RESPONSE_TRIGGER_RATING,
  COMING_SOON_POST_DIRECTORIES,
  MAIN_POST_DIRECTORIES,
  MAIN_POST_DIRECTORY_ALIASES,
  OTHER_DIRECTORIES_STATUS_KEY,
  SOCIAL_CONNECTION_BY_DIRECTORY,
  SOCIAL_POST_TYPES,
  SOCIAL_POST_VISUAL_RULES,
} from '../constants';
import { POST_STATUS_TONE } from '../theme/visibility.palette';
import {
  DashboardComparison,
  DeltaDirection,
  LocationPatch,
  StatsRange,
  StatsRangePreset,
  UberallAutoResponseTrigger,
  UberallCompletenessLevelPayload,
  UberallMetricPayload,
  UberallMetricSeriesPayload,
  UberallPeriodPayload,
  UberallSocialConnectionsPayload,
  UberallSocialPostStatus,
  UberallSocialPostType,
} from '../type';

const ISO_DATE = 'YYYY-MM-DD';

export const rangeFromPreset = (preset: StatsRangePreset, reference: Date = new Date()): StatsRange => {
  const to = moment(reference);
  const from = moment(reference);

  switch (preset) {
    case 'LAST_7_DAYS':
      from.subtract(6, 'days');
      break;
    case 'LAST_30_DAYS':
      from.subtract(29, 'days');
      break;
    case 'LAST_90_DAYS':
      from.subtract(89, 'days');
      break;
    case 'LAST_6_MONTHS':
      from.subtract(6, 'months').add(1, 'day');
      break;
    case 'LAST_12_MONTHS':
      from.subtract(12, 'months').add(1, 'day');
      break;
    default:
      from.subtract(29, 'days');
      break;
  }

  return { preset, from: from.format(ISO_DATE), to: to.format(ISO_DATE) };
};

/**
 * A directory's display name. Uberall sends codes (APPLE_MAPS, PRATIQUE…) and adds new ones without
 * notice, so an unknown code is shown as words rather than as a raw translation key.
 */
export const directoryLabel = (t: (key: string, options?: Record<string, unknown>) => string, directory: string) =>
  t(`visibility.posts.platforms.${directory}`, {
    defaultValue: directory
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' '),
  });

// The back end refuses a window it should not read, and that refusal fails the whole dashboard.
const MAX_COMPARISON_DAYS = 366;

/** Whether the back end will accept this custom window: in order, not in the future, at most a year. */
export const isComparisonRangeValid = (from: string, to: string, today: Date = new Date()) => {
  const start = moment(from, ISO_DATE, true);
  const end = moment(to, ISO_DATE, true);
  if (!start.isValid() || !end.isValid() || end.isBefore(start)) return false;
  if (end.isAfter(moment(today), 'day')) return false;
  return end.diff(start, 'days') <= MAX_COMPARISON_DAYS;
};

/**
 * The comparison window to ask for, or null to let the back end use the equally long window right
 * before the period. A CUSTOM choice that is incomplete or invalid falls back to that default too.
 */
export const comparisonWindow = (range: StatsRange, comparison: DashboardComparison): ComparisonWindow | null => {
  if (comparison.preset === 'PREVIOUS_YEAR') {
    return {
      compareStartDate: moment(range.from).subtract(1, 'year').format(ISO_DATE),
      compareEndDate: moment(range.to).subtract(1, 'year').format(ISO_DATE),
    };
  }
  if (
    comparison.preset === 'CUSTOM' &&
    comparison.from &&
    comparison.to &&
    isComparisonRangeValid(comparison.from, comparison.to)
  ) {
    return { compareStartDate: comparison.from, compareEndDate: comparison.to };
  }
  return null;
};

export const formatRangeLabel = ({ from, to }: StatsRange, locale: string) => {
  const start = moment(from).locale(locale);
  const end = moment(to).locale(locale);
  return `${start.format('D MMM YYYY')} – ${end.format('D MMM YYYY')}`;
};

/** The same wording as the range selector, for the two windows the payload says it compared. */
export const formatPeriod = ({ startDate, endDate }: UberallPeriodPayload, locale: string) =>
  formatRangeLabel({ preset: 'LAST_30_DAYS', from: startDate, to: endDate }, locale);

export const imageBackground = (url: string) => `url("${url}") center / cover no-repeat`;

export const mediaBackground = (url: string | null | undefined, fallback: string) =>
  url ? imageBackground(url) : fallback;

export const readFieldValue = (raw: string, type?: string): string | number | null => {
  if (type !== 'number') return raw;
  if (raw.trim() === '') return null;
  const parsed = Number(raw);
  return Number.isNaN(parsed) ? null : parsed;
};

export const toMuiPageIndex = (page: number) => Math.max(0, page - 1);

export const fromMuiPageIndex = (index: number) => index + 1;

export const isReplyValid = (content: string, maxLength: number) => {
  const trimmed = content.trim();
  return trimmed.length > 0 && trimmed.length <= maxLength;
};

export const levelFillPercent = ({ earned, weight }: UberallCompletenessLevelPayload) =>
  weight > 0 ? Math.round((earned / weight) * 100) : 0;

export const ringGeometry = (percent: number, radius: number) => {
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.min(100, Math.max(0, percent));
  return {
    circumference,
    dashOffset: circumference * (1 - clamped / 100),
  };
};

export const formatCompactNumber = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(value);

export const formatCountOrDash = (value: number | null, locale: string) =>
  value === null ? '—' : formatCompactNumber(value, locale);

export const formatDelta = (deltaPercent: number | null, locale: string) => {
  if (deltaPercent === null) return null;

  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(Math.abs(deltaPercent))}%`;
};

/** Magnitude of an absolute change, sign-free for the same reason as formatDelta. */
export const formatDeltaAbsolute = (delta: number, locale: string, digits = 0) =>
  new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(
    Math.abs(delta),
  );

export const deltaDirection = (deltaPercent: number | null | undefined): DeltaDirection => {
  if (deltaPercent === null || deltaPercent === undefined) return 'neutral';

  const printed = Math.round(deltaPercent * 10) / 10;
  if (printed === 0) return 'neutral';

  return printed > 0 ? 'up' : 'down';
};

export const formatRating = (rating: number | null, locale: string) =>
  rating === null
    ? '—'
    : new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(rating);

export const relativeDate = (isoDate: string | null, locale: string) =>
  isoDate ? moment(isoDate).locale(locale).fromNow() : '—';

export const formatDate = (isoDate: string | null, locale: string, format = 'D MMM YYYY') =>
  isoDate ? moment(isoDate).locale(locale).format(format) : '—';

export const seoReportFileName = (generatedAt: string) =>
  `rapport-seo-${moment.utc(generatedAt).format('YYYY-MM-DD')}.pdf`;

export const saveFile = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
};

export const starsFor = (rating: number | null, scale = 5) => {
  if (rating === null) return '';
  const filled = Math.round(rating);
  return '★'.repeat(Math.min(filled, scale)) + '☆'.repeat(Math.max(0, scale - filled));
};

export const monthGrid = (monthIso: string) => {
  const first = moment(monthIso).startOf('month');
  const start = first.clone().startOf('isoWeek');
  const end = moment(monthIso).endOf('month').endOf('isoWeek');
  const days: { date: string; isCurrentMonth: boolean }[] = [];

  for (const cursor = start.clone(); cursor.isSameOrBefore(end, 'day'); cursor.add(1, 'day')) {
    days.push({ date: cursor.format(ISO_DATE), isCurrentMonth: cursor.isSame(first, 'month') });
  }

  return days;
};

// Same order as the backend: Google first, then the social networks, any other directory, and the
// secondary group last.
const STATUS_FALLBACK_DIRECTORIES = ['GOOGLE', 'GOOGLE_MAPS', 'FACEBOOK', 'INSTAGRAM'];
const GOOGLE_STATUS_DIRECTORIES = ['GOOGLE', 'GOOGLE_MAPS'];
// Same list as the backend: only a failure overrides the post-level status, so a pending Google
// entry never hides APPROVAL_NEEDED or INCOMPLETE.
export const FAILED_POST_STATUSES: UberallSocialPostStatus[] = ['FAILED', 'REJECTED', 'IMPOSSIBLE'];

const googlePostStatus = (statusPerDirectory: Record<string, string>): UberallSocialPostStatus | null => {
  for (const directory of GOOGLE_STATUS_DIRECTORIES) {
    const raw = statusPerDirectory[directory];
    const known = raw ? knownPostStatus(raw) : null;
    if (known && known !== 'UNKNOWN') return known;
  }
  return null;
};

/**
 * Uberall drops the post-level status once the publication time has passed, so it is rebuilt from the
 * directories: the first known status wins, and an unknown one stays visible as UNKNOWN. A Google
 * failure wins over everything: Uberall keeps PUBLISHED on top when Google failed but another
 * directory published.
 */
export const effectivePostStatus = (post: {
  status: UberallSocialPostStatus | null;
  statusPerDirectory: Record<string, string>;
}): { status: UberallSocialPostStatus | null; fromDirectory: boolean } => {
  const google = googlePostStatus(post.statusPerDirectory ?? {});
  if (google && FAILED_POST_STATUSES.includes(google)) return { status: google, fromDirectory: true };
  if (post.status !== null && post.status !== 'UNKNOWN') return { status: post.status, fromDirectory: false };

  const perDirectory = post.statusPerDirectory ?? {};
  const rank = (directory: string) => {
    if (directory === OTHER_DIRECTORIES_STATUS_KEY) return STATUS_FALLBACK_DIRECTORIES.length + 1;
    const index = STATUS_FALLBACK_DIRECTORIES.indexOf(directory);
    return index === -1 ? STATUS_FALLBACK_DIRECTORIES.length : index;
  };
  const ordered = Object.keys(perDirectory).sort((left, right) => rank(left) - rank(right));

  for (const directory of ordered) {
    const known = knownPostStatus(perDirectory[directory]);
    if (known && known !== 'UNKNOWN') return { status: known, fromDirectory: true };
  }
  if (ordered.length > 0 || post.status === 'UNKNOWN') return { status: 'UNKNOWN', fromDirectory: ordered.length > 0 };
  return { status: null, fromDirectory: false };
};

/** Names Google in the label when Google is the directory that failed. */
export const postStatusLabelKey = (
  post: { statusPerDirectory: Record<string, string> },
  status: UberallSocialPostStatus,
): string =>
  status === 'FAILED' && googlePostStatus(post.statusPerDirectory ?? {}) === 'FAILED'
    ? 'visibility.posts.statusFailedOnGoogle'
    : `visibility.posts.statuses.${status}`;

/** A per-directory status as a known post status, or null for one we do not model. Uberall's raw
 * string comes through unnormalised, so the case is not to be trusted. */
export const knownPostStatus = (status: string): UberallSocialPostStatus | null => {
  const upper = status.toUpperCase();
  return Object.hasOwnProperty.call(POST_STATUS_TONE, upper) ? (upper as UberallSocialPostStatus) : null;
};

/** A status is null while Uberall has not reported it yet. */
export type DirectoryStatus =
  | { kind: 'directory'; directory: string; status: string | null }
  | { kind: 'others'; count: number; status: string | null };

const canonicalDirectory = (directory: string) => MAIN_POST_DIRECTORY_ALIASES[directory] ?? directory;

/**
 * One entry per main directory the post targets or Uberall reports, then a single entry for every
 * secondary one: Uberall reports those only as a group, under OTHER_DIRECTORIES_STATUS_KEY.
 */
export const postDirectoryStatuses = (post: {
  directories: string[] | null;
  statusPerDirectory: Record<string, string>;
}): DirectoryStatus[] => {
  const reported: Record<string, string> = {};
  for (const [directory, status] of Object.entries(post.statusPerDirectory ?? {})) {
    reported[canonicalDirectory(directory)] = status;
  }
  const requested = (post.directories ?? []).map(canonicalDirectory);
  const isMain = (directory: string) => MAIN_POST_DIRECTORIES.includes(directory);

  const main = MAIN_POST_DIRECTORIES.filter(directory => requested.includes(directory) || directory in reported);
  const others = requested.filter(directory => !isMain(directory));
  const othersStatus = reported[OTHER_DIRECTORIES_STATUS_KEY] ?? null;

  const entries: DirectoryStatus[] = main.map(directory => ({
    kind: 'directory',
    directory,
    status: reported[directory] ?? null,
  }));
  if (others.length > 0 || othersStatus !== null)
    entries.push({ kind: 'others', count: others.length, status: othersStatus });
  return entries;
};

/** What is known of the post's visual; a null type is unknown and never blocks a directory. */
export interface PostVisualInfo {
  type: string | null;
  sizeBytes: number;
}

export const visualInfoFromFile = (file: File): PostVisualInfo => ({ type: file.type || null, sizeBytes: file.size });

export type PostDirectoryBlocker =
  | 'COMING_SOON'
  | 'NOT_CONNECTED'
  | 'CONNECTIONS_UNKNOWN'
  | 'INSTAGRAM_NOT_LINKED'
  | 'NEEDS_POST_TYPE'
  | 'NEEDS_VISUAL'
  | 'VISUAL_FORMAT'
  | 'VISUAL_TOO_LARGE';

export interface PostDirectoryContext {
  /** Null while loading or after a failed load: the social directories then stay closed. */
  connections: UberallSocialConnectionsPayload | null;
  /** The last read failed: the networks are closed for an unknown reason, not because they are disconnected. */
  hasConnectionsError: boolean;
  postType: UberallSocialPostType;
  visual: PostVisualInfo | null;
}

export const postDirectoryBlocker = (
  directory: string,
  { connections, hasConnectionsError, postType, visual }: PostDirectoryContext,
): PostDirectoryBlocker | null => {
  if (COMING_SOON_POST_DIRECTORIES.includes(directory)) return 'COMING_SOON';
  const connectionKey = SOCIAL_CONNECTION_BY_DIRECTORY[directory];
  if (!connectionKey) return null;
  if (!connections) return hasConnectionsError ? 'CONNECTIONS_UNKNOWN' : 'NOT_CONNECTED';
  if (!connections[connectionKey]?.connected) {
    // With Facebook in, Instagram still needs its own connection, made in Connectors.
    return directory === 'INSTAGRAM' && connections.facebook?.connected ? 'INSTAGRAM_NOT_LINKED' : 'NOT_CONNECTED';
  }
  if (!SOCIAL_POST_TYPES.includes(postType)) return 'NEEDS_POST_TYPE';

  const rule = SOCIAL_POST_VISUAL_RULES[directory];
  if (!rule) return null;
  if (!visual) return rule.isRequired ? 'NEEDS_VISUAL' : null;
  if (visual.type !== null && !rule.types.includes(visual.type)) return 'VISUAL_FORMAT';
  if (visual.sizeBytes > rule.maxBytes) return 'VISUAL_TOO_LARGE';
  return null;
};

/** The format message names what each directory refuses, so it is worded per directory. */
export const postDirectoryBlockerKey = (blocker: PostDirectoryBlocker, directory: string) =>
  blocker === 'VISUAL_FORMAT'
    ? `visibility.posts.blockers.VISUAL_FORMAT_${directory}`
    : `visibility.posts.blockers.${blocker}`;

export const withoutBlockedDirectories = (directories: string[], context: PostDirectoryContext) =>
  directories.filter(directory => postDirectoryBlocker(directory, context) === null);

export const brandInitials = (name: string) =>
  name
    .split(/[\s-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join('') || '?';

const relativeLuminance = (hex: string) => {
  const channel = (offset: number) => Number.parseInt(hex.slice(offset, offset + 2), 16) / 255;
  const linear = (value: number) => (value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4);

  return 0.2126 * linear(channel(0)) + 0.7152 * linear(channel(2)) + 0.0722 * linear(channel(4));
};

const contrastRatio = (left: number, right: number) => (Math.max(left, right) + 0.05) / (Math.min(left, right) + 0.05);

export const readableInkOn = (background: string, light = '#FFFFFF', dark = '#1A1A1A') => {
  const hex = background.replace('#', '');
  if (hex.length !== 6) return light;

  const fill = relativeLuminance(hex);
  const onLight = contrastRatio(fill, relativeLuminance(light.replace('#', '')));
  const onDark = contrastRatio(fill, relativeLuminance(dark.replace('#', '')));

  return onDark > onLight ? dark : light;
};

export const sendableLocationPatch = (patch: LocationPatch, canWriteServiceAreas: boolean): LocationPatch => {
  if (patch.serviceAreas === undefined) return patch;

  const { serviceAreas, ...rest } = patch;
  if (!canWriteServiceAreas || serviceAreas === null) return rest;

  return { ...rest, serviceAreas: serviceAreas.filter(area => area.name && area.placeId) };
};

/**
 * A template's emoji, or one inferred from the star band it answers — the field was added after the
 * first templates were written, so a null there is common and an empty tile reads as broken.
 */
export const replyTemplateEmoji = (template: { emoji: string | null; minRating: number | null }) => {
  if (template.emoji) return template.emoji;
  if (template.minRating !== null && template.minRating >= 4) return '😊';
  if (template.minRating !== null && template.minRating <= 2) return '🙏';

  return '💬';
};

export interface ActiveAutoResponseTrigger {
  trigger: string;
  ruleId: string;
  ruleName: string | null;
}

/**
 * Shared by the rule dialog's Save gate and the rules list's quick-toggle Switch, so activating a
 * rule is refused consistently everywhere, not only where a merchant happens to open the dialog.
 */
export const assessAutoResponseActivation = (
  ruleId: string | null,
  triggers: UberallAutoResponseTrigger[],
  activeTriggers: ActiveAutoResponseTrigger[],
) => {
  const unattendedLowRatings = triggers.filter(
    trigger => AUTO_RESPONSE_TRIGGER_RATING[trigger] < AUTO_RESPONSE_MIN_UNATTENDED_RATING,
  );
  const claimed = activeTriggers.filter(
    claim => claim.ruleId !== ruleId && triggers.includes(claim.trigger as UberallAutoResponseTrigger),
  );

  return { unattendedLowRatings, claimed, allowed: unattendedLowRatings.length === 0 && claimed.length === 0 };
};

/**
 * The views series: Uberall reports impressions split four ways (desktop/mobile × maps/search), and
 * `visibility.impressions` is their sum — so the chart has to add them bucket by bucket or its total
 * disagrees with the tile above it. Granularity is Uberall's own choice, taken from the payload.
 *
 * Returns null when no impression series came back, which is what a location with no insights data
 * looks like: the panel then says so rather than drawing a flat zero line.
 */
export const viewsSeriesOf = (analytics: {
  series?: UberallMetricSeriesPayload[] | null;
}): UberallMetricSeriesPayload | null => {
  const impressions = (analytics.series ?? []).filter(series => series.name.startsWith('BUSINESS_IMPRESSIONS'));
  if (impressions.length === 0) return null;

  const totals = new Map<string, number>();
  for (const series of impressions) {
    for (const point of series.points) {
      totals.set(point.period, (totals.get(point.period) ?? 0) + point.count);
    }
  }

  const points = Array.from(totals, ([period, count]) => ({ period, count })).sort((left, right) =>
    left.period.localeCompare(right.period),
  );

  return points.length === 0 ? null : { name: 'BUSINESS_IMPRESSIONS', granularity: impressions[0].granularity, points };
};
